import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getProfileImage } from '../../utils/profileImageUtils';

const HelperGirlProfiles = ({ helperEmail, tripPlanId }) => {
  const [girlProfiles, setGirlProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHelperGirlProfiles = async () => {
      try {
        setLoading(true);
        console.log('HelperGirlProfiles: Fetching for', { helperEmail, tripPlanId });
        
        // Check if helperEmail is valid
        if (!helperEmail || !tripPlanId) {
          console.log('HelperGirlProfiles: Invalid parameters', { helperEmail, tripPlanId });
          setGirlProfiles([]);
          return;
        }
        
        // Get bookings for this trip plan by this helper
        const bookingsRef = collection(db, 'bookings');
        
        // Try to find bookings by userEmail first, then by userName
        let bookingsQuery = query(
          bookingsRef, 
          where('tripPlanId', '==', tripPlanId),
          where('userEmail', '==', helperEmail)
        );
        console.log('HelperGirlProfiles: Querying bookings with userEmail', { tripPlanId, helperEmail });
        let bookingsSnapshot = await getDocs(bookingsQuery);
        
        // If no bookings found by userEmail, try userName
        if (bookingsSnapshot.empty && !helperEmail.includes('@')) {
          bookingsQuery = query(
            bookingsRef, 
            where('tripPlanId', '==', tripPlanId),
            where('userName', '==', helperEmail)
          );
          console.log('HelperGirlProfiles: Querying bookings with userName', { tripPlanId, helperEmail });
          bookingsSnapshot = await getDocs(bookingsQuery);
        }
        
        // Also get all bookings for this trip plan to see what's available
        const allBookingsRef = collection(db, 'bookings');
        const allBookingsQuery = query(
          allBookingsRef, 
          where('tripPlanId', '==', tripPlanId)
        );
        const allBookingsSnapshot = await getDocs(allBookingsQuery);
        const allBookings = [];
        allBookingsSnapshot.forEach(doc => {
          const bookingData = doc.data();
          allBookings.push({
            id: doc.id,
            ...bookingData
          });
        });
        console.log('HelperGirlProfiles: All bookings for this trip plan', allBookings);
        console.log('HelperGirlProfiles: Booking details:', allBookings.map(b => ({
          id: b.id,
          userEmail: b.userEmail,
          userName: b.userName,
          girlId: b.girlId,
          status: b.status
        })));
        
        const bookings = [];
        bookingsSnapshot.forEach(doc => {
          const bookingData = doc.data();
          bookings.push({
            id: doc.id,
            ...bookingData
          });
        });

        console.log('HelperGirlProfiles: Found bookings for helper', bookings.length);
        console.log('HelperGirlProfiles: All bookings data', bookings);
        console.log('HelperGirlProfiles: Booking search method used', bookingsSnapshot.empty ? 'userName' : 'userEmail');

        // Get girl profiles for these bookings
        const girlProfiles = [];
        for (const booking of bookings) {
          if (booking.girlId) {
            try {
              // Get girl profile from girls collection
              const girlRef = doc(db, 'girls', booking.girlId);
              const girlSnap = await getDoc(girlRef);
              
              if (girlSnap.exists()) {
                const girlData = girlSnap.data();
                const profileImageUrl = getProfileImage(girlData);
                console.log('HelperGirlProfiles: Girl data for', booking.girlId, {
                  nickname: girlData.nickname,
                  images: girlData.images,
                  profileImage: girlData.profileImage,
                  profileImageUrl: profileImageUrl
                });
                if (profileImageUrl) {
                  girlProfiles.push({
                    id: booking.girlId,
                    ...girlData,
                    profileImage: profileImageUrl,
                    bookingStatus: booking.status || 'pending',
                    bookingId: booking.id
                  });
                }
              }
            } catch (error) {
              console.error('Error fetching girl profile:', error);
            }
          }
        }

        console.log('HelperGirlProfiles: Found girl profiles', girlProfiles.length);
        console.log('HelperGirlProfiles: Final girl profiles data', girlProfiles);
        setGirlProfiles(girlProfiles);
      } catch (error) {
        console.error('Error fetching helper girl profiles:', error);
        setGirlProfiles([]);
      } finally {
        setLoading(false);
      }
    };

    if (helperEmail && tripPlanId) {
      fetchHelperGirlProfiles();
    }
  }, [helperEmail, tripPlanId]);

  if (loading) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
        <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
        <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse"></div>
      </div>
    );
  }

  if (girlProfiles.length === 0) {
    return (
      <div className="flex items-center space-x-2">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
          <span className="text-gray-400 text-xs">👤</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      {girlProfiles.map((girl, index) => (
        <div key={girl.id} className="relative">
          <img
            src={girl.profileImage}
            alt={girl.displayName || girl.email}
            className={`w-12 h-12 rounded-full object-cover border-2 ${
              girl.bookingStatus === 'confirmed' 
                ? 'border-green-500' 
                : girl.bookingStatus === 'pending'
                ? 'border-yellow-500'
                : 'border-red-500'
            }`}
          />
          <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs ${
            girl.bookingStatus === 'confirmed' 
              ? 'bg-green-500 text-white' 
              : girl.bookingStatus === 'pending'
              ? 'bg-yellow-500 text-white'
              : 'bg-red-500 text-white'
          }`}>
            {girl.bookingStatus === 'confirmed' ? '✓' : 
             girl.bookingStatus === 'pending' ? '⏰' : '?'}
          </div>
        </div>
      ))}
    </div>
  );
};

export default HelperGirlProfiles;
